// Iter 82.12 / Phase E3.2 Sprint 2 — Period Close Workflow page.
//
// Operator-locked surface:
//   - List existing closes (with status, certificates)
//   - Pre-flight gate check + Close dialog (4-eye-lite enforced server-side)
//   - Reopen dialog (primary super-admin only, mandatory reason ≥10 chars)
//   - Reclose dialog
//   - Latest Period Close Certificate display (with verification badge)
//   - Audit trail viewer
import { useCallback, useEffect, useState } from "react";
import {
    Scales, Lock, LockOpen, Certificate, ShieldCheck, ShieldWarning,
    ArrowsClockwise, CheckCircle, WarningCircle, ClockCounterClockwise,
    DownloadSimple,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return inr(v);
};
const thisMonthYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const STATUS_TONE = {
    Open: "bg-amber-100 text-amber-800 border-amber-200",
    Closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ReopenedTemporarily: "bg-rose-100 text-rose-800 border-rose-200",
};

function StatusBadge({ status }) {
    return (
        <Badge className={`${STATUS_TONE[status] || "bg-slate-100"} text-xs`} data-testid={`close-status-${status}`}>
            {status}
        </Badge>
    );
}

function CertificateCard({ cert, closeId }) {
    const downloadPdf = async () => {
        try {
            const r = await api.get(
                `/payroll/period/close/${closeId}/certificate.pdf?cert_version=${cert.cert_version}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `PeriodCloseCertificate_${cert.period_month}_v${cert.cert_version}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Certificate PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "PDF render failed"));
        }
    };
    return (
        <div className="border rounded-lg bg-white p-4 space-y-2" data-testid={`certificate-v${cert.cert_version}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Certificate size={16} className="text-emerald-600" />
                    <span className="text-sm font-semibold" data-testid="cert-version">
                        Period Close Certificate · v{cert.cert_version}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {cert.verified ? (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs" data-testid="cert-verified">
                            <ShieldCheck size={12} className="inline mr-1" /> Hash verified
                        </Badge>
                    ) : (
                        <Badge className="bg-rose-100 text-rose-800 text-xs" data-testid="cert-tampered">
                            <ShieldWarning size={12} className="inline mr-1" /> Hash MISMATCH
                        </Badge>
                    )}
                    {closeId && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadPdf}
                            data-testid={`cert-download-pdf-${cert.cert_version}`}
                        >
                            <DownloadSimple size={12} className="mr-1" /> PDF
                        </Button>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                <div data-testid="cert-period"><span className="text-slate-500">Period</span><div className="font-mono">{cert.period_month}</div></div>
                <div data-testid="cert-closed-by"><span className="text-slate-500">Closed By</span><div className="font-mono">{cert.closed_by_email}</div></div>
                <div data-testid="cert-closed-at"><span className="text-slate-500">Closed On</span><div className="font-mono">{cert.closed_at?.slice(0, 19).replace("T", " ")}</div></div>
                <div data-testid="cert-employee-count"><span className="text-slate-500">Employee Count</span><div className="font-mono">{cert.employee_count}</div></div>
                <div data-testid="cert-run-count"><span className="text-slate-500">Run Count</span><div className="font-mono">{cert.run_count}</div></div>
                <div data-testid="cert-reopened-count"><span className="text-slate-500">Reopened Count</span><div className="font-mono">{cert.reopened_count}</div></div>
                <div data-testid="cert-gross"><span className="text-slate-500">Gross Payroll</span><div className="font-mono font-semibold">{compact(cert.gross_payroll)}</div></div>
                <div data-testid="cert-net"><span className="text-slate-500">Net Payroll</span><div className="font-mono font-semibold">{compact(cert.net_payroll)}</div></div>
                <div data-testid="cert-employer"><span className="text-slate-500">Employer Contribs</span><div className="font-mono font-semibold">{compact(cert.employer_contributions)}</div></div>
            </div>
            <div className="text-[10px] text-slate-400 font-mono break-all border-t pt-2" data-testid="cert-hash">
                SHA-256: {cert.certificate_hash}
            </div>
        </div>
    );
}

function PreCheckBlock({ pre, onClose, busy }) {
    const canClose = pre && pre.blockers.length === 0;
    return (
        <div className="space-y-2" data-testid="precheck-block">
            <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-slate-400" />
                <span className="text-xs uppercase text-slate-500 tracking-wide">Pre-Close Gates</span>
            </div>
            {pre.blockers.length === 0 ? (
                <div className="flex items-center gap-1.5 text-emerald-700 text-sm" data-testid="precheck-no-blockers">
                    <CheckCircle size={14} /> All pre-close gates passed.
                </div>
            ) : (
                <div className="space-y-1" data-testid="precheck-blockers">
                    {pre.blockers.map((b, i) => (
                        <div key={i} className="text-xs bg-rose-50 border border-rose-200 rounded p-2 text-rose-700">
                            <span className="font-mono font-semibold">{b.code}</span> — {b.message}
                        </div>
                    ))}
                </div>
            )}
            {pre.warnings.length > 0 && (
                <div className="space-y-1" data-testid="precheck-warnings">
                    {pre.warnings.map((w, i) => (
                        <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                            <WarningCircle size={12} className="inline mr-1" />
                            <span className="font-mono font-semibold">{w.code}</span> — {w.message}
                        </div>
                    ))}
                </div>
            )}
            <div className="text-xs text-slate-600 mt-2">
                Included Locked Runs: <span className="font-mono">{pre.included_run_ids.length}</span> ·
                Total Net Pay: <span className="font-mono font-semibold">{compact((pre.expected_totals || {}).net_pay)}</span>
            </div>
            <Button onClick={onClose} disabled={!canClose || busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="close-confirm-btn">
                <Lock size={14} className="mr-1" />
                {busy ? "Closing…" : `Close Period ${pre.period_month}`}
            </Button>
        </div>
    );
}

export default function PayrollPeriodClose() {
    const [periodMonth, setPeriodMonth] = useState(thisMonthYM());
    const [closes, setCloses] = useState([]);
    const [current, setCurrent] = useState(null);
    const [pre, setPre] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closeRemarks, setCloseRemarks] = useState("");
    const [reopenDialog, setReopenDialog] = useState({ open: false, closeId: null });
    const [reopenReason, setReopenReason] = useState("");
    const [recloseDialog, setRecloseDialog] = useState({ open: false, closeId: null });
    const [auditDrawer, setAuditDrawer] = useState({ open: false, items: [] });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, cur] = await Promise.all([
                api.get("/payroll/period/close/list"),
                api.get("/payroll/period/close/current", { params: { period_month: periodMonth } }),
            ]);
            setCloses(list.data.items || []);
            setCurrent(cur.data);
            // Pre-check only if no active close exists for this period
            if (!cur.data.exists || cur.data.status === "ReopenedTemporarily") {
                const pc = await api.get("/payroll/period/close/pre-check", {
                    params: { period_month: periodMonth },
                });
                setPre(pc.data);
            } else {
                setPre(null);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load period close data"));
        } finally {
            setLoading(false);
        }
    }, [periodMonth]);
    useEffect(() => { reload(); }, [reload]);

    const doClose = async () => {
        setBusy(true);
        try {
            const r = await api.post("/payroll/period/close", {
                period_month: periodMonth, remarks: closeRemarks,
            });
            toast.success(`Period ${periodMonth} closed. Certificate v1 issued.`);
            setCloseDialogOpen(false);
            setCloseRemarks("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to close period"));
        } finally { setBusy(false); }
    };

    const doReopen = async () => {
        if (reopenReason.length < 10) {
            toast.error("Reason must be at least 10 characters");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/payroll/period/close/${reopenDialog.closeId}/reopen`, {
                reason: reopenReason,
            });
            toast.success("Period reopened. Re-close within 7 days.");
            setReopenDialog({ open: false, closeId: null });
            setReopenReason("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to reopen"));
        } finally { setBusy(false); }
    };

    const doReclose = async () => {
        setBusy(true);
        try {
            await api.post(`/payroll/period/close/${recloseDialog.closeId}/reclose`, {
                remarks: closeRemarks,
            });
            toast.success("Period re-closed. New certificate version issued.");
            setRecloseDialog({ open: false, closeId: null });
            setCloseRemarks("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to reclose"));
        } finally { setBusy(false); }
    };

    const openAudit = async (closeId) => {
        try {
            const r = await api.get(`/payroll/period/close/${closeId}/audit`);
            setAuditDrawer({ open: true, items: r.data.items || [] });
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load audit"));
        }
    };

    if (loading) return <div className="p-6 text-slate-500" data-testid="pc-loading">Loading period close…</div>;

    const showPre = pre && pre.included_run_ids !== undefined;
    const showCloseAction = showPre && (!current?.exists || current?.status === "ReopenedTemporarily");
    const currentCerts = (current?.certificates || []).slice().reverse();
    const latestCert = currentCerts[0];

    return (
        <div className="space-y-4" data-testid="payroll-period-close-page">
            <PageHeader
                eyebrow="Payroll Operations · Period Close"
                title={`Period Close · ${periodMonth}`}
                description="Formal book-close gate. 4-eye-lite enforced. Snapshot persisted at close (D11). Tamper-evident certificate (D12)."
                actions={(
                    <div className="flex items-center gap-2">
                        <Input
                            type="month"
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="h-9 w-40"
                            data-testid="pc-period-picker"
                        />
                        <Button variant="outline" size="sm" onClick={reload} data-testid="pc-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                )}
            />

            {/* Current period state */}
            <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="pc-current-state">
                <div className="flex items-center gap-3">
                    <Scales size={20} className="text-slate-400" />
                    <div className="flex-1">
                        <div className="text-xs uppercase text-slate-500 tracking-wide">Current State · {periodMonth}</div>
                        <div className="text-sm text-slate-700 mt-0.5">
                            {current?.exists
                                ? `Status: ${current.status} · Closed at ${(current.closed_at || "").slice(0, 19).replace("T", " ")} by ${current.closed_by_email} · Reopened ${current.reopened_count}× · Certificate(s): ${(current.certificates || []).length}`
                                : "Period has NOT been closed yet."}
                        </div>
                    </div>
                    <StatusBadge status={current?.status || "Open"} />
                    {current?.exists && (
                        <Button variant="outline" size="sm" onClick={() => openAudit(current.id)} data-testid="pc-audit-btn">
                            <ClockCounterClockwise size={14} className="mr-1" /> Audit
                        </Button>
                    )}
                </div>

                {/* Pre-check / Close action */}
                {showCloseAction && pre && (
                    <PreCheckBlock pre={pre} busy={busy}
                        onClose={() => {
                            if (current?.status === "ReopenedTemporarily") {
                                setRecloseDialog({ open: true, closeId: current.id });
                            } else {
                                setCloseDialogOpen(true);
                            }
                        }} />
                )}

                {current?.exists && current.status === "Closed" && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                        <Button variant="outline" size="sm"
                            onClick={() => setReopenDialog({ open: true, closeId: current.id })}
                            data-testid="pc-reopen-btn">
                            <LockOpen size={14} className="mr-1" /> Reopen (Primary Admin only · 7-day window)
                        </Button>
                    </div>
                )}
            </div>

            {/* Latest certificate */}
            {latestCert && <CertificateCard cert={latestCert} closeId={current?.id} />}

            {/* Certificate history */}
            {currentCerts.length > 1 && (
                <div className="space-y-2" data-testid="pc-cert-history">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Earlier Certificate Versions</div>
                    {currentCerts.slice(1).map((c) => <CertificateCard key={c.cert_version} cert={c} closeId={current?.id} />)}
                </div>
            )}

            {/* All closes table */}
            <div className="border rounded-lg bg-white p-4" data-testid="pc-all-closes">
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">All Period Closes</div>
                {closes.length === 0 ? (
                    <div className="text-sm text-slate-400" data-testid="pc-closes-empty">No closes recorded yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                            <tr><th className="px-3 py-2 text-left">Period</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Closed By</th><th className="px-3 py-2 text-left">Closed At</th><th className="px-3 py-2 text-left">Runs</th><th className="px-3 py-2 text-left">Reopens</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {closes.map((c) => (
                                <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setPeriodMonth(c.period_month); }} data-testid={`pc-close-row-${c.period_month}`}>
                                    <td className="px-3 py-2 text-xs font-mono font-semibold">{c.period_month}</td>
                                    <td className="px-3 py-2 text-xs"><StatusBadge status={c.status} /></td>
                                    <td className="px-3 py-2 text-xs">{c.closed_by_email}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{(c.closed_at || "").slice(0, 19).replace("T", " ")}</td>
                                    <td className="px-3 py-2 text-xs">{(c.included_run_ids || []).length}</td>
                                    <td className="px-3 py-2 text-xs">{c.reopened_count || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Close dialog */}
            <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
                <DialogContent data-testid="close-dialog">
                    <DialogHeader>
                        <DialogTitle>Close Period {periodMonth}</DialogTitle>
                        <DialogDescription>
                            This appends an immutable Period Close Certificate (D12) and persists
                            a KPI snapshot (D11). The action is 4-eye-lite: the user who locked the
                            last run in this period CANNOT close it.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs uppercase text-slate-500">Remarks (optional, max 500 chars)</label>
                        <Textarea
                            value={closeRemarks}
                            onChange={(e) => setCloseRemarks(e.target.value)}
                            placeholder="e.g. Closing Jan-2026 payroll after final attendance reconciliation."
                            data-testid="close-remarks-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
                        <Button onClick={doClose} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="close-dialog-confirm">
                            {busy ? "Closing…" : "Confirm Close"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reopen dialog */}
            <Dialog open={reopenDialog.open} onOpenChange={(open) => setReopenDialog({ open, closeId: open ? reopenDialog.closeId : null })}>
                <DialogContent data-testid="reopen-dialog">
                    <DialogHeader>
                        <DialogTitle>Reopen Period {periodMonth}</DialogTitle>
                        <DialogDescription>
                            Reopen is restricted to the Primary Super-Admin and MUST occur within
                            7 days of close (D3, D4). Provide a reason (≥ 10 characters) for the
                            permanent audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs uppercase text-slate-500">Reason (mandatory, ≥10 chars)</label>
                        <Textarea
                            value={reopenReason}
                            onChange={(e) => setReopenReason(e.target.value)}
                            placeholder="e.g. Late attendance correction for EMP-0042 — needs to re-flow to TDS."
                            data-testid="reopen-reason-input"
                        />
                        <div className="text-[10px] text-slate-400">{reopenReason.length}/10 chars minimum</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReopenDialog({ open: false, closeId: null })}>Cancel</Button>
                        <Button onClick={doReopen} disabled={busy || reopenReason.length < 10}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="reopen-dialog-confirm">
                            {busy ? "Reopening…" : "Confirm Reopen"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reclose dialog */}
            <Dialog open={recloseDialog.open} onOpenChange={(open) => setRecloseDialog({ open, closeId: open ? recloseDialog.closeId : null })}>
                <DialogContent data-testid="reclose-dialog">
                    <DialogHeader>
                        <DialogTitle>Re-close Period {periodMonth}</DialogTitle>
                        <DialogDescription>
                            Re-close after a Reopen. A new certificate version with the latest KPI
                            snapshot will be appended. 4-eye-lite enforced.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs uppercase text-slate-500">Remarks (optional)</label>
                        <Textarea
                            value={closeRemarks}
                            onChange={(e) => setCloseRemarks(e.target.value)}
                            data-testid="reclose-remarks-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRecloseDialog({ open: false, closeId: null })}>Cancel</Button>
                        <Button onClick={doReclose} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="reclose-dialog-confirm">
                            {busy ? "Re-closing…" : "Confirm Re-close"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Audit drawer */}
            <Sheet open={auditDrawer.open} onOpenChange={(open) => setAuditDrawer({ open, items: open ? auditDrawer.items : [] })}>
                <SheetContent className="w-[480px] sm:max-w-[480px]" data-testid="audit-drawer">
                    <SheetHeader>
                        <SheetTitle>Period Close Audit Trail</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 space-y-2 max-h-[calc(100vh-100px)] overflow-y-auto">
                        {auditDrawer.items.length === 0 ? (
                            <div className="text-sm text-slate-400">No audit events.</div>
                        ) : auditDrawer.items.map((a) => (
                            <div key={a.id} className="border-l-2 border-slate-200 pl-3 py-1.5" data-testid={`audit-${a.action}`}>
                                <div className="text-xs font-semibold">{a.action.toUpperCase()}</div>
                                <div className="text-[10px] text-slate-500 font-mono">{a.created_at?.slice(0, 19).replace("T", " ")}</div>
                                <div className="text-[10px] text-slate-500">by {a.actor_email} ({a.actor_role})</div>
                                {a.payload && Object.keys(a.payload).length > 0 && (
                                    <pre className="text-[10px] bg-slate-50 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(a.payload, null, 2)}</pre>
                                )}
                            </div>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
